/**
 * Frontend encryption utility for sensitive payment data
 * 
 * Provides client-side encryption for sensitive fields before transmission.
 * Uses Web Crypto API for AES-GCM encryption with secure key exchange.
 * 
 * Security Features:
 * - AES-256-GCM encryption
 * - Secure random IV generation
 * - Field-level encryption (preserves JSON structure)
 * - Base64 encoding for transport
 * - Error handling with fallback to unencrypted data
 */

class PaymentEncryptionUtil {
    constructor() {
        this.algorithm = 'AES-GCM';
        this.keyLength = 256;
        this.ivLength = 12; // 96 bits for GCM
        this.encryptionKey = null;
        this.isEnabled = process.env.REACT_APP_ENCRYPTION_ENABLED !== 'false';
    }

    /**
     * Initialize encryption with a base64-encoded key
     * @param {string} base64Key - Base64 encoded encryption key
     */
    async initialize(base64Key) {
        if (!this.isEnabled || !base64Key) {
            console.warn('Frontend encryption disabled or no key provided');
            return;
        }

        try {
            const keyData = this.base64ToArrayBuffer(base64Key);
            this.encryptionKey = await crypto.subtle.importKey(
                'raw',
                keyData,
                { name: this.algorithm },
                false,
                ['encrypt', 'decrypt']
            );
        } catch (error) {
            console.error('Failed to initialize encryption:', error);
            this.encryptionKey = null;
        }
    }

    /**
     * Check if encryption is available and enabled
     */
    isAvailable() {
        return this.isEnabled && this.encryptionKey && window.crypto && window.crypto.subtle;
    }

    /**
     * Encrypt sensitive fields in a payment object
     * @param {Object} paymentData - Payment data object
     * @returns {Object} Object with sensitive fields encrypted
     */
    async encryptPaymentData(paymentData) {
        if (!this.isAvailable() || !paymentData) {
            return paymentData;
        }

        try {
            const encryptedData = { ...paymentData };
            
            // Define sensitive fields that should be encrypted
            const sensitiveFields = [
                'billingName',
                'billingStreet', 
                'billingCity',
                'billingState',
                'billingZip',
                'cardNumber',
                'cvv',
                'securityCode'
            ];

            // Encrypt each sensitive field if it exists
            for (const field of sensitiveFields) {
                if (encryptedData[field] && typeof encryptedData[field] === 'string') {
                    const encryptedValue = await this.encryptString(encryptedData[field]);
                    if (encryptedValue) {
                        encryptedData[field] = encryptedValue;
                    }
                }
            }

            // Handle nested billing address object
            if (encryptedData.billingAddress && typeof encryptedData.billingAddress === 'object') {
                const billingSensitiveFields = ['name', 'street', 'city', 'state', 'zip'];
                for (const field of billingSensitiveFields) {
                    if (encryptedData.billingAddress[field] && typeof encryptedData.billingAddress[field] === 'string') {
                        const encryptedValue = await this.encryptString(encryptedData.billingAddress[field]);
                        if (encryptedValue) {
                            encryptedData.billingAddress[field] = encryptedValue;
                        }
                    }
                }
            }

            return encryptedData;
        } catch (error) {
            console.error('Payment data encryption failed:', error);
            // Return original data if encryption fails
            return paymentData;
        }
    }

    /**
     * Decrypt sensitive fields in a payment response
     * @param {Object} encryptedData - Response data with encrypted fields
     * @returns {Object} Object with sensitive fields decrypted
     */
    async decryptPaymentData(encryptedData) {
        if (!this.isAvailable() || !encryptedData) {
            return encryptedData;
        }

        try {
            const decryptedData = { ...encryptedData };
            
            const sensitiveFields = [
                'billingName',
                'billingStreet',
                'billingCity', 
                'billingState',
                'billingZip'
            ];

            // Decrypt each sensitive field if it exists
            for (const field of sensitiveFields) {
                if (decryptedData[field] && typeof decryptedData[field] === 'string') {
                    const decryptedValue = await this.decryptString(decryptedData[field]);
                    if (decryptedValue) {
                        decryptedData[field] = decryptedValue;
                    }
                }
            }

            // Handle nested billing address object
            if (decryptedData.billingAddress && typeof decryptedData.billingAddress === 'object') {
                const billingSensitiveFields = ['name', 'street', 'city', 'state', 'zip'];
                for (const field of billingSensitiveFields) {
                    if (decryptedData.billingAddress[field] && typeof decryptedData.billingAddress[field] === 'string') {
                        const decryptedValue = await this.decryptString(decryptedData.billingAddress[field]);
                        if (decryptedValue) {
                            decryptedData.billingAddress[field] = decryptedValue;
                        }
                    }
                }
            }

            return decryptedData;
        } catch (error) {
            console.error('Payment data decryption failed:', error);
            // Return original data if decryption fails (might not be encrypted)
            return encryptedData;
        }
    }

    /**
     * Encrypt a single string value
     * @param {string} plaintext - The string to encrypt
     * @returns {string|null} Base64 encoded encrypted data with IV, or null on failure
     */
    async encryptString(plaintext) {
        if (!this.isAvailable() || !plaintext) {
            return null;
        }

        try {
            // Generate random IV
            const iv = crypto.getRandomValues(new Uint8Array(this.ivLength));
            
            // Encrypt the data
            const encoder = new TextEncoder();
            const data = encoder.encode(plaintext);
            
            const encryptedData = await crypto.subtle.encrypt(
                {
                    name: this.algorithm,
                    iv: iv
                },
                this.encryptionKey,
                data
            );

            // Combine IV and encrypted data
            const combined = new Uint8Array(iv.length + encryptedData.byteLength);
            combined.set(iv, 0);
            combined.set(new Uint8Array(encryptedData), iv.length);

            // Return as base64
            return this.arrayBufferToBase64(combined.buffer);
        } catch (error) {
            console.error('String encryption failed:', error);
            return null;
        }
    }

    /**
     * Decrypt a single string value  
     * @param {string} encryptedBase64 - Base64 encoded encrypted data with IV
     * @returns {string|null} Decrypted plaintext, or null on failure
     */
    async decryptString(encryptedBase64) {
        if (!this.isAvailable() || !encryptedBase64) {
            return null;
        }

        try {
            // Decode from base64
            const combined = new Uint8Array(this.base64ToArrayBuffer(encryptedBase64));
            
            // Extract IV and encrypted data
            const iv = combined.slice(0, this.ivLength);
            const encryptedData = combined.slice(this.ivLength);

            // Decrypt the data
            const decryptedData = await crypto.subtle.decrypt(
                {
                    name: this.algorithm,
                    iv: iv
                },
                this.encryptionKey,
                encryptedData
            );

            // Convert back to string
            const decoder = new TextDecoder();
            return decoder.decode(decryptedData);
        } catch (error) {
            console.error('String decryption failed:', error);
            return null;
        }
    }

    /**
     * Convert ArrayBuffer to base64 string
     */
    arrayBufferToBase64(buffer) {
        const bytes = new Uint8Array(buffer);
        let binary = '';
        for (let i = 0; i < bytes.byteLength; i++) {
            binary += String.fromCharCode(bytes[i]);
        }
        return btoa(binary);
    }

    /**
     * Convert base64 string to ArrayBuffer
     */
    base64ToArrayBuffer(base64) {
        const binaryString = atob(base64);
        const bytes = new Uint8Array(binaryString.length);
        for (let i = 0; i < binaryString.length; i++) {
            bytes[i] = binaryString.charCodeAt(i);
        }
        return bytes.buffer;
    }
}

// Create singleton instance
const paymentEncryptionUtil = new PaymentEncryptionUtil();

// Initialize encryption on load if key is available
const initializeEncryption = async () => {
    const encryptionKey = process.env.REACT_APP_ENCRYPTION_KEY;
    if (encryptionKey) {
        await paymentEncryptionUtil.initialize(encryptionKey);
    }
};

// Initialize when module loads
initializeEncryption().catch(console.error);

export default paymentEncryptionUtil;